/* Multi-column Pro — grid, step carousel, continuous scroll and coverflow */

(() => {
  class MultiColumnPro {
    constructor(root) {
      if (!root || root.dataset.mcpInitialized === 'true') return;

      this.root = root;
      this.track = root.querySelector('[data-mcp-track]');
      this.viewport = root.querySelector('[data-mcp-viewport]');
      this.slides = Array.from(root.querySelectorAll('[data-mcp-slide]'));
      this.dots = Array.from(root.querySelectorAll('[data-mcp-dot]'));
      this.previousButton = root.querySelector('[data-mcp-prev]');
      this.nextButton = root.querySelector('[data-mcp-next]');

      if (!this.track || !this.viewport || this.slides.length < 1) return;

      root.dataset.mcpInitialized = 'true';
      this.desktopLayout = root.dataset.desktopLayout || 'standard';
      this.mobileLayout = root.dataset.mobileLayout || 'standard';
      this.motion = root.dataset.motion || 'none';
      this.desktopVisible = Math.max(1, Number(root.dataset.visible) || 3);
      this.mobileDisplay = root.dataset.mobileDisplay || 'carousel';
      this.autoplayInterval = Math.max(3000, Number(root.dataset.interval) || 8000);
      this.continuousDuration = Math.max(10, Number(root.dataset.continuousSpeed) || 30);
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.layout = 'standard';
      this.isCoverflow = false;
      this.current = 0;
      this.startX = null;
      this.startY = null;
      this.autoplayTimer = null;
      this.resizeTimer = null;
      this.stepResetTimer = null;
      this.stepPhysical = 0;
      this.stepCloneCount = 0;
      this.pauseReasons = new Set();
      this.visibilityObserver = null;

      this.bindEvents();
      this.setupLayout();
      this.startAutoplay();
      this.observeVisibility();
    }

    visibleCount() {
      if (this.mobileQuery.matches) return 1;
      return Math.min(this.desktopVisible, this.slides.length);
    }

    carouselIsActive() {
      if (this.isCoverflow) return this.slides.length > 1;
      if (this.mobileQuery.matches) {
        return this.mobileDisplay === 'carousel' && this.slides.length > 1;
      }
      return this.slides.length > this.visibleCount();
    }

    normalizeIndex(index) {
      return ((index % this.slides.length) + this.slides.length) % this.slides.length;
    }

    updateDots() {
      this.dots.forEach((dot, index) => {
        dot.setAttribute('aria-current', index === this.current ? 'true' : 'false');
      });
    }

    createClone(slide, index) {
      const clone = slide.cloneNode(true);
      clone.dataset.mcpClone = 'true';
      clone.dataset.cloneIndex = String(index);
      clone.removeAttribute('data-block-id');
      clone.removeAttribute('id');
      clone.removeAttribute('data-shopify-editor-block');
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
      return clone;
    }

    removeClones() {
      this.track.querySelectorAll('[data-mcp-clone]').forEach((clone) => clone.remove());
    }

    resetLayout() {
      this.removeClones();
      this.root.classList.remove('multi-column-pro--standard-slider', 'multi-column-pro--has-slider');
      this.track.style.removeProperty('transform');
      this.track.style.removeProperty('transition');
      this.track.style.removeProperty('animation');
      this.viewport.style.removeProperty('height');
      this.root.style.removeProperty('--mcp-loop-distance');
      this.root.style.removeProperty('--mcp-continuous-duration');
      this.root.style.removeProperty('--mcp-item-width');
      this.root.style.removeProperty('--mcp-standard-card-height');
      this.root.style.removeProperty('--mcp-coverflow-card-height');
      this.root.style.removeProperty('--mcp-coverflow-viewport-height');
      window.clearTimeout(this.stepResetTimer);
      this.stepCloneCount = 0;

      this.slides.forEach((slide) => {
        slide.style.removeProperty('height');
        slide.style.removeProperty('transform');
        slide.style.removeProperty('opacity');
        slide.style.removeProperty('z-index');
        slide.style.removeProperty('filter');
        slide.style.removeProperty('transition');
        slide.classList.remove('multi-column-pro__coverflow-visible');
        slide.removeAttribute('aria-hidden');
      });
    }

    measureStandardCards() {
      this.root.style.removeProperty('--mcp-standard-card-height');

      const maximumHeight = Math.max(
        ...this.slides.map((slide) => {
          const card = slide.querySelector('.multi-column-pro__card');
          return card ? Math.ceil(card.scrollHeight) : 0;
        }),
        1
      );

      this.root.style.setProperty('--mcp-standard-card-height', `${maximumHeight}px`);
    }

    measureCoverflow() {
      this.root.style.removeProperty('--mcp-coverflow-card-height');
      this.slides.forEach((slide) => slide.style.removeProperty('height'));

      const maximumHeight = Math.max(
        ...this.slides.map((slide) => Math.ceil(slide.scrollHeight)),
        1
      );
      const verticalBuffer = this.mobileQuery.matches ? 48 : 64;

      this.root.style.setProperty('--mcp-coverflow-card-height', `${maximumHeight}px`);
      this.root.style.setProperty('--mcp-coverflow-viewport-height', `${maximumHeight + verticalBuffer}px`);
    }

    applyCoverflow(index, animate = true) {
      this.current = this.normalizeIndex(index);
      const viewportWidth = this.viewport.getBoundingClientRect().width;
      const step = viewportWidth * (this.mobileQuery.matches ? 0.43 : 0.18);
      const visibleRadius = this.mobileQuery.matches ? 1 : 2;

      this.slides.forEach((slide, slideIndex) => {
        let distance = slideIndex - this.current;
        if (distance > this.slides.length / 2) distance -= this.slides.length;
        if (distance < -this.slides.length / 2) distance += this.slides.length;

        const absoluteDistance = Math.abs(distance);
        const scale = Math.max(0.68, 1 - absoluteDistance * 0.105);
        const opacity = absoluteDistance === 0 ? 1 : absoluteDistance === 1 ? 0.88 : absoluteDistance === 2 ? 0.48 : 0.2;
        const translateX = distance * step;
        const translateY = absoluteDistance * (this.mobileQuery.matches ? 8 : 14);

        if (!animate || this.reduceMotion.matches) slide.style.transition = 'none';
        slide.style.transform = `translateX(calc(-50% + ${translateX}px)) translateY(${translateY}px) scale(${scale})`;
        slide.style.opacity = absoluteDistance <= visibleRadius ? String(opacity) : '0';
        slide.style.zIndex = String(100 - absoluteDistance);
        slide.style.filter = `brightness(${Math.max(0.72, 1 - absoluteDistance * 0.09)})`;
        slide.classList.toggle('multi-column-pro__coverflow-visible', absoluteDistance <= visibleRadius);
        slide.setAttribute('aria-hidden', absoluteDistance <= visibleRadius ? 'false' : 'true');

        if (!animate || this.reduceMotion.matches) {
          requestAnimationFrame(() => slide.style.removeProperty('transition'));
        }
      });

      this.updateDots();
    }

    setupStep() {
      this.removeClones();
      this.stepCloneCount = Math.min(this.visibleCount(), this.slides.length);
      const before = document.createDocumentFragment();

      this.slides.slice(-this.stepCloneCount).forEach((slide) => {
        before.appendChild(this.createClone(slide, this.slides.indexOf(slide)));
      });
      this.track.insertBefore(before, this.track.firstChild);

      this.slides.slice(0, this.stepCloneCount).forEach((slide) => {
        this.track.appendChild(this.createClone(slide, this.slides.indexOf(slide)));
      });

      this.stepPhysical = this.stepCloneCount + this.current;
    }

    applyStepPosition(index, animate = true) {
      const visible = this.visibleCount();
      const previousCurrent = this.current;
      const targetCurrent = this.normalizeIndex(index);

      if (this.stepCloneCount > 0 && animate && index === previousCurrent + 1) {
        this.stepPhysical += 1;
      } else if (this.stepCloneCount > 0 && animate && index === previousCurrent - 1) {
        this.stepPhysical -= 1;
      } else {
        this.stepPhysical = this.stepCloneCount + targetCurrent;
      }

      this.current = targetCurrent;
      const firstSlide = this.slides[0];
      const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      const itemDistance = firstSlide.getBoundingClientRect().width + gap;
      const distance = this.stepPhysical * itemDistance;

      if (!animate || this.reduceMotion.matches) this.track.style.transition = 'none';
      this.track.style.transform = `translate3d(-${distance}px, 0, 0)`;

      this.slides.forEach((slide, slideIndex) => {
        const visibleNow = slideIndex >= this.current && slideIndex < this.current + visible;
        slide.setAttribute('aria-hidden', visibleNow ? 'false' : 'true');
      });
      this.updateDots();

      if (!animate || this.reduceMotion.matches) {
        requestAnimationFrame(() => this.track.style.removeProperty('transition'));
      } else if (this.stepCloneCount > 0) {
        window.clearTimeout(this.stepResetTimer);
        this.stepResetTimer = window.setTimeout(() => {
          if (this.stepPhysical < this.stepCloneCount) this.stepPhysical += this.slides.length;
          if (this.stepPhysical >= this.stepCloneCount + this.slides.length) this.stepPhysical -= this.slides.length;
          this.track.style.transition = 'none';
          this.track.style.transform = `translate3d(-${this.stepPhysical * itemDistance}px, 0, 0)`;
          // Commit the invisible clone-to-original jump before transitions return.
          void this.track.offsetWidth;
          requestAnimationFrame(() => this.track.style.removeProperty('transition'));
        }, 500);
      }
    }

    setupContinuous() {
      this.removeClones();
      this.slides.forEach((slide, index) => {
        this.track.appendChild(this.createClone(slide, index));
      });

      requestAnimationFrame(() => {
        const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
        const distance = this.slides.reduce(
          (sum, slide) => sum + slide.getBoundingClientRect().width,
          0
        ) + gap * this.slides.length;

        this.root.style.setProperty('--mcp-loop-distance', distance.toFixed(2));
        this.root.style.setProperty('--mcp-continuous-duration', `${this.continuousDuration}s`);
      });
    }

    setStandardItemWidth() {
      const visible = this.visibleCount();
      const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      const availableWidth = this.track.getBoundingClientRect().width;
      const itemWidth = Math.max(1, (availableWidth - gap * (visible - 1)) / visible);
      this.root.style.setProperty('--mcp-item-width', `${itemWidth.toFixed(2)}px`);
    }

    setupLayout() {
      this.resetLayout();
      this.layout = this.mobileQuery.matches ? this.mobileLayout : this.desktopLayout;
      this.isCoverflow = this.layout === 'coverflow';
      this.root.classList.toggle('multi-column-pro--layout-standard', !this.isCoverflow);
      this.root.classList.toggle('multi-column-pro--layout-coverflow', this.isCoverflow);
      this.current = this.normalizeIndex(this.current);
      this.root.style.setProperty('--mcp-columns', String(this.visibleCount()));

      if (this.isCoverflow) {
        this.measureCoverflow();
        this.root.classList.toggle('multi-column-pro--has-slider', this.slides.length > 1);
        this.applyCoverflow(this.current, false);
        return;
      }

      this.measureStandardCards();

      if (!this.carouselIsActive()) {
        this.current = 0;
        this.updateDots();
        return;
      }

      this.root.classList.add('multi-column-pro--standard-slider', 'multi-column-pro--has-slider');
      this.setStandardItemWidth();
      if (this.motion === 'continuous' && !this.reduceMotion.matches) {
        this.setupContinuous();
      } else {
        this.setupStep();
        this.applyStepPosition(this.current, false);
      }
    }

    goTo(index, animate = true) {
      if (this.isCoverflow) {
        this.applyCoverflow(index, animate);
      } else if (this.carouselIsActive() && this.motion !== 'continuous') {
        this.applyStepPosition(index, animate);
      }
    }

    next() {
      this.goTo(this.current + 1);
    }

    previous() {
      this.goTo(this.current - 1);
    }

    stopAutoplay() {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
      this.root.classList.add('multi-column-pro--paused');
    }

    startAutoplay() {
      this.stopAutoplay();
      if (
        this.pauseReasons.size ||
        this.reduceMotion.matches ||
        !this.carouselIsActive() ||
        this.motion === 'none'
      ) return;

      this.root.classList.remove('multi-column-pro--paused');
      if (this.motion === 'continuous' && !this.isCoverflow) return;
      this.autoplayTimer = window.setInterval(() => this.next(), this.autoplayInterval);
    }

    pause(reason) {
      this.pauseReasons.add(reason);
      this.stopAutoplay();
    }

    resume(reason) {
      this.pauseReasons.delete(reason);
      this.startAutoplay();
    }

    scheduleLayout() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => {
        this.setupLayout();
        this.startAutoplay();
      }, 100);
    }

    bindEvents() {
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          this.goTo(Number(dot.dataset.mcpDot));
          this.pause('manual');
          window.setTimeout(() => this.resume('manual'), this.autoplayInterval);
        });
      });

      if (this.previousButton) {
        this.previousButton.addEventListener('click', () => {
          this.previous();
          this.pause('manual');
          window.setTimeout(() => this.resume('manual'), this.autoplayInterval);
        });
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => {
          this.next();
          this.pause('manual');
          window.setTimeout(() => this.resume('manual'), this.autoplayInterval);
        });
      }

      this.slides.forEach((slide, index) => {
        slide.addEventListener('click', (event) => {
          if (!this.isCoverflow || index === this.current) return;
          event.preventDefault();
          this.goTo(index);
          this.pause('manual');
          window.setTimeout(() => this.resume('manual'), this.autoplayInterval);
        });

        slide.querySelectorAll('img').forEach((image) => {
          if (!image.complete) image.addEventListener('load', () => this.scheduleLayout(), { once: true });
        });
      });

      this.viewport.addEventListener('touchstart', (event) => {
        this.startX = event.touches[0].clientX;
        this.startY = event.touches[0].clientY;
        this.pause('touch');
      }, { passive: true });

      this.viewport.addEventListener('touchend', (event) => {
        if (this.startX === null || this.startY === null) {
          this.resume('touch');
          return;
        }

        const deltaX = this.startX - event.changedTouches[0].clientX;
        const deltaY = this.startY - event.changedTouches[0].clientY;
        if (
          this.carouselIsActive() &&
          this.motion !== 'continuous' &&
          Math.abs(deltaX) > 45 &&
          Math.abs(deltaX) > Math.abs(deltaY)
        ) {
          deltaX > 0 ? this.next() : this.previous();
        }

        this.startX = null;
        this.startY = null;
        window.setTimeout(() => this.resume('touch'), 120);
      }, { passive: true });

      this.viewport.addEventListener('touchcancel', () => {
        this.startX = null;
        this.startY = null;
        this.resume('touch');
      });

      this.viewport.addEventListener('keydown', (event) => {
        if (!this.carouselIsActive() || this.motion === 'continuous') return;
        if (event.key === 'ArrowRight') this.next();
        if (event.key === 'ArrowLeft') this.previous();
      });

      this.root.addEventListener('mouseenter', () => this.pause('hover'));
      this.root.addEventListener('mouseleave', () => this.resume('hover'));
      this.root.addEventListener('focusin', () => this.pause('focus'));
      this.root.addEventListener('focusout', (event) => {
        if (!this.root.contains(event.relatedTarget)) this.resume('focus');
      });

      this.mobileQuery.addEventListener('change', () => this.scheduleLayout());
      window.addEventListener('resize', () => this.scheduleLayout(), { passive: true });
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.scheduleLayout());
      }
      document.addEventListener('visibilitychange', () => {
        document.hidden ? this.pause('hidden') : this.resume('hidden');
      });

      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:block:select', (event) => {
          if (event.detail.sectionId !== this.root.dataset.sectionId) return;
          const selectedIndex = this.slides.findIndex(
            (slide) => slide.dataset.blockId === event.detail.blockId
          );
          if (selectedIndex >= 0) {
            this.goTo(selectedIndex, false);
            this.pause('editor');
          }
        });
      }
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) return;
      this.visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          entry.isIntersecting ? this.resume('visibility') : this.pause('visibility');
        });
      }, { threshold: 0.2 });
      this.visibilityObserver.observe(this.root);
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-mcp-section]').forEach((root) => new MultiColumnPro(root));
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
